/**
 * HomeStories.js
 * ==============
 * Facebook / Instagram Stories-style full-screen experience.
 *
 * Behaviour:
 *  - Auto-opens on FIRST homepage visit per session (gated by sessionStorage).
 *  - 4 chapters: Daily Needs · International · Promos · News.
 *  - Each chapter has multiple slides; auto-advances every 6s.
 *  - Tap left half → previous slide. Tap right half → next slide.
 *  - Swipe left/right (touch) works the same.
 *  - Progress bars at the top (Instagram style).
 *  - X button → closes overlay → user lands on regular HomePage scroll.
 *  - "Skip to website" link is always visible.
 *  - "View Stories" floating button (top-right of HomePage) re-opens any time.
 *
 * Design tokens follow BCI brand: black bg, #D4AF37 gold, Cinzel/Playfair fonts.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Sparkles, Newspaper, Globe2, Gift, Package, Pause, Play } from 'lucide-react';
import { API } from '@/context/AuthContext';

const SLIDE_DURATION_MS = 6000;

const FALLBACK_DAILY = [
  {
    image: 'https://images.unsplash.com/photo-1604335079441-9bf38de3d6a3?w=1600&q=85',
    title: 'Laundry at your doorstep',
    subtitle: 'Wash, iron & fold — picked up and delivered',
    cta: { label: 'Order on WhatsApp', href: '/daily-needs' },
  },
  {
    image: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=1600&q=85',
    title: 'Home nurse · spa · car wash',
    subtitle: 'Trusted vendors, member-only rates',
    cta: { label: 'Browse services', href: '/daily-needs' },
  },
  {
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&q=85',
    title: 'Provisions delivered',
    subtitle: 'From neighbourhood stores you already trust',
    cta: { label: 'Open Daily Needs', href: '/daily-needs' },
  },
];

const FALLBACK_INTL = [
  {
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1600&q=85',
    title: 'Resort access · India & abroad',
    subtitle: 'One card. A growing world of affiliations.',
    cta: { label: 'See partners', href: '/affiliations' },
  },
  {
    image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1600&q=85',
    title: 'Luxury hotels',
    subtitle: 'Curated stays at member-only rates',
    cta: { label: 'Explore stays', href: '/affiliations' },
  },
  {
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1600&q=85',
    title: 'Wellness & spa partners',
    subtitle: 'Rejuvenation across India',
    cta: { label: 'Discover wellness', href: '/affiliations' },
  },
];

const FALLBACK_PROMOS = [
  {
    image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1600&q=85',
    title: 'Member-only offers',
    subtitle: 'Up to 50% off at premium partners',
    cta: { label: 'View offers', href: '/offers' },
  },
];

const CHAPTERS_META = [
  { key: 'daily',    label: 'Daily Needs',    icon: Package,   accent: '#10B981' },
  { key: 'intl',     label: 'International',  icon: Globe2,    accent: '#D4AF37' },
  { key: 'promos',   label: 'Promos',         icon: Gift,      accent: '#F59E0B' },
  { key: 'news',     label: 'News',           icon: Newspaper, accent: '#A78BFA' },
];

const STORAGE_KEY = 'bitz-stories-seen-v1';

export const HomeStories = ({ openSignal = 0 }) => {
  const [open, setOpen] = useState(false);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [chapters, setChapters] = useState({
    daily:  FALLBACK_DAILY,
    intl:   FALLBACK_INTL,
    promos: FALLBACK_PROMOS,
    news:   [],
  });

  // Touch swipe support
  const touchStartX = useRef(null);

  // Hold-to-pause support
  const pauseTimerRef = useRef(null);

  // ---- Auto-open on first visit -----------------------------------------
  useEffect(() => {
    try {
      const seen = sessionStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_) { /* ignore */ }
  }, []);

  // ---- Manual open via parent (e.g. floating button) --------------------
  useEffect(() => {
    if (openSignal > 0) {
      setChapterIdx(0);
      setSlideIdx(0);
      setProgress(0);
      setOpen(true);
    }
  }, [openSignal]);

  // ---- Fetch live data --------------------------------------------------
  useEffect(() => {
    if (!open) return;
    // Daily Needs categories
    axios.get(`${API}/daily-needs/categories`).then(r => {
      const cats = (r.data || []).filter(c => c.image_url).slice(0, 4);
      if (cats.length >= 2) {
        const mapped = cats.map(c => ({
          image: c.image_url,
          title: c.name,
          subtitle: c.description || 'Member-only doorstep service',
          cta: { label: 'Open Daily Needs', href: `/daily-needs?category=${c.id}` },
        }));
        setChapters(prev => ({ ...prev, daily: mapped }));
      }
    }).catch(() => {});
    // Partners → International
    axios.get(`${API}/partners?is_active=true&limit=6`).then(r => {
      const arr = (r.data || []).filter(p => p.image_url || (p.photos && p.photos[0])).slice(0, 4);
      if (arr.length >= 2) {
        const mapped = arr.map(p => ({
          image: p.image_url || p.photos?.[0] || p.logo_url,
          title: p.name || 'Affiliated partner',
          subtitle: p.location || p.city || 'Partner network · BCI',
          cta: { label: 'See affiliations', href: '/affiliations' },
        }));
        setChapters(prev => ({ ...prev, intl: mapped }));
      }
    }).catch(() => {});
    // Offers → Promos
    axios.get(`${API}/offers?is_active=true`).then(r => {
      const arr = (r.data || []).filter(o => o.image_url).slice(0, 4);
      if (arr.length >= 1) {
        const mapped = arr.map(o => ({
          image: o.image_url,
          title: o.title,
          subtitle: o.discount_text || o.description || 'Member-only offer',
          cta: { label: 'Claim offer', href: '/offers' },
        }));
        setChapters(prev => ({ ...prev, promos: mapped }));
      }
    }).catch(() => {});
    // News
    axios.get(`${API}/news?limit=8`).then(r => {
      const arr = (r.data || []).filter(n => n.image_url || n.cover_image_url).slice(0, 5);
      if (arr.length >= 1) {
        const mapped = arr.map(n => ({
          image: n.cover_image_url || n.image_url,
          title: n.title,
          subtitle: n.summary || (n.content || '').slice(0, 120),
          cta: { label: 'Read more', href: n.slug ? `/news/${n.slug}` : '/news' },
        }));
        setChapters(prev => ({ ...prev, news: mapped }));
      } else {
        // Soft fallback so the chapter is never empty
        setChapters(prev => ({ ...prev, news: [{
          image: 'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=1600&q=85',
          title: 'BCI News & Updates',
          subtitle: 'The latest from your member ecosystem',
          cta: { label: 'Open news', href: '/news' },
        }] }));
      }
    }).catch(() => {});
  }, [open]);

  const chapterKeys = useMemo(() => CHAPTERS_META.map(c => c.key), []);
  const currentChapter = CHAPTERS_META[chapterIdx];
  const currentSlides = chapters[currentChapter.key] || [];
  const currentSlide = currentSlides[slideIdx];

  // ---- Auto-advance timer -----------------------------------------------
  useEffect(() => {
    if (!open || paused || !currentSlide) return;
    setProgress(0);
    const start = Date.now();
    const intv = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / SLIDE_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(intv);
        advance();
      }
    }, 60);
    return () => clearInterval(intv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paused, chapterIdx, slideIdx, currentSlide]);

  const close = useCallback(() => {
    setOpen(false);
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
  }, []);

  const advance = useCallback(() => {
    setProgress(0);
    if (slideIdx + 1 < currentSlides.length) {
      setSlideIdx(slideIdx + 1);
    } else if (chapterIdx + 1 < chapterKeys.length) {
      setChapterIdx(chapterIdx + 1);
      setSlideIdx(0);
    } else {
      close();
    }
  }, [slideIdx, currentSlides.length, chapterIdx, chapterKeys.length, close]);

  const regress = useCallback(() => {
    setProgress(0);
    if (slideIdx > 0) {
      setSlideIdx(slideIdx - 1);
    } else if (chapterIdx > 0) {
      const prevKey = chapterKeys[chapterIdx - 1];
      const prevLen = (chapters[prevKey] || []).length || 1;
      setChapterIdx(chapterIdx - 1);
      setSlideIdx(prevLen - 1);
    }
  }, [slideIdx, chapterIdx, chapterKeys, chapters]);

  // ---- Keyboard ---------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') advance();
      else if (e.key === 'ArrowLeft') regress();
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, advance, regress, close]);

  // ---- Touch swipe ------------------------------------------------------
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    // Hold-to-pause: trigger pause after 250ms
    pauseTimerRef.current = setTimeout(() => setPaused(true), 250);
  };
  const handleTouchEnd = (e) => {
    if (pauseTimerRef.current) { clearTimeout(pauseTimerRef.current); pauseTimerRef.current = null; }
    setPaused(false);
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 60) regress();
    else if (dx < -60) advance();
    touchStartX.current = null;
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="stories-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] bg-black"
        data-testid="home-stories-overlay"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Background image with crossfade */}
        <AnimatePresence mode="wait">
          {currentSlide && (
            <motion.img
              key={`${currentChapter.key}-${slideIdx}`}
              src={currentSlide.image}
              alt={currentSlide.title}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          )}
        </AnimatePresence>
        {/* Vignette + readability gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/20 to-black/90 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />

        {/* Progress bars (top) */}
        <div className="absolute top-0 left-0 right-0 z-20 px-3 pt-3 flex gap-1.5">
          {CHAPTERS_META.map((ch, i) => (
            <div key={ch.key} className="flex-1 flex gap-1">
              {(chapters[ch.key] || [{}]).map((_, j) => {
                let pct = 0;
                if (i < chapterIdx) pct = 100;
                else if (i === chapterIdx && j < slideIdx) pct = 100;
                else if (i === chapterIdx && j === slideIdx) pct = progress;
                return (
                  <div key={j} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full"
                      style={{ width: `${pct}%`, transition: pct === 0 ? 'none' : 'width 60ms linear' }}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Chapter header (just below progress) */}
        <div className="absolute top-7 left-0 right-0 z-20 px-4 pt-2 flex items-center justify-between">
          <div className="flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10">
            <currentChapter.icon className="w-3.5 h-3.5" style={{ color: currentChapter.accent }} />
            <span className="text-white text-xs font-semibold tracking-wide uppercase" style={{ letterSpacing: '0.15em' }}>
              {currentChapter.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPaused(p => !p)}
              className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              aria-label={paused ? 'Play' : 'Pause'}
              data-testid="stories-pause-btn"
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button
              onClick={close}
              className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              aria-label="Close stories"
              data-testid="stories-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tap zones (left/right) */}
        <button
          onClick={regress}
          className="absolute top-0 left-0 bottom-0 w-1/3 z-10 flex items-center justify-start pl-2 opacity-0 hover:opacity-100 transition-opacity"
          aria-label="Previous"
          data-testid="stories-prev-zone"
        >
          <span className="hidden md:flex w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/10 items-center justify-center text-white">
            <ChevronLeft className="w-4 h-4" />
          </span>
        </button>
        <button
          onClick={advance}
          className="absolute top-0 right-0 bottom-0 w-1/3 z-10 flex items-center justify-end pr-2 opacity-0 hover:opacity-100 transition-opacity"
          aria-label="Next"
          data-testid="stories-next-zone"
        >
          <span className="hidden md:flex w-9 h-9 rounded-full bg-black/40 backdrop-blur-md border border-white/10 items-center justify-center text-white">
            <ChevronRight className="w-4 h-4" />
          </span>
        </button>

        {/* Slide caption */}
        {currentSlide && (
          <motion.div
            key={`${currentChapter.key}-${slideIdx}-caption`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="absolute left-0 right-0 bottom-0 z-20 px-6 sm:px-10 pb-20 sm:pb-24 pointer-events-none"
          >
            <div className="max-w-2xl pointer-events-auto">
              <span
                className="inline-block text-[10px] tracking-[0.3em] uppercase mb-3 px-2 py-1 rounded-full border"
                style={{ color: currentChapter.accent, borderColor: `${currentChapter.accent}55`, background: 'rgba(0,0,0,0.35)' }}
              >
                {currentChapter.label}
              </span>
              <h2
                className="text-3xl sm:text-5xl font-bold text-white mb-3 leading-tight"
                style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 4px 24px rgba(0,0,0,0.6)' }}
              >
                {currentSlide.title}
              </h2>
              {currentSlide.subtitle && (
                <p className="text-base sm:text-lg text-gray-200 mb-5 max-w-xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>
                  {currentSlide.subtitle}
                </p>
              )}
              {currentSlide.cta && (
                <Link
                  to={currentSlide.cta.href}
                  onClick={close}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-semibold rounded-full hover:bg-[#E6D699] transition-colors text-sm sm:text-base"
                  data-testid="stories-cta-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  {currentSlide.cta.label}
                </Link>
              )}
            </div>
          </motion.div>
        )}

        {/* Skip to website */}
        <button
          onClick={close}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 text-white/70 hover:text-white text-xs tracking-[0.25em] uppercase px-4 py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/10 transition-colors"
          data-testid="stories-skip-btn"
        >
          Skip to website
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default HomeStories;
